import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useToasts } from "react-toast-notifications";

import { Table, Button, Flex, Box, Text } from "../../components";
import { TEXTS } from "../../constants";
import { useCheckPermission, useIsActionAllowed } from "../../hooks";
import {
  ITeamUserObject,
  USER_IAM_ROLE,
  USER_SEATS_STATUS,
} from "types/TeamUsersResponse";
import DeleteUserModal from "./DeleteUserModal";
import ProfileActionCol from "./ProfileActionCol";
import ProfileNameCol from "./ProfileNameCol";
import { useTeamUsersQuery } from "./api/useTeamUsersQuery";
import ChangeInvitationModal from "./ChangeInvitationModal";
import ManageUsersStatusFilter, {
  FILTER_DISPLAY_VALUES,
} from "./ManageUsersStatusFilter";
import SearchInput from "features/devices/components/SearchInput";
import {
  IManageUsersTable,
  ManageUsersTableColumnParams,
  SelectedRow,
  ISelectedUserToEdit,
  TUserTypeFilter,
  USER_TYPE_LABELS,
} from "./types";
import { useUnassignSeatsMutation } from "./api/useUnassignSeatsMutation";
import ChangeRoleModal from "./ChangeRoleModal";
import ChangeRoleColumn from "./ChangeRoleColumn";
import ManageUserTypeFilter from "./ManageUserTypeFilter";
import { useTeamUserTypesQuery } from "./api/useTeamUserTypesQuery";
import { FEATURES, FEATURE_ACTION } from "constants/features";
import { useQueryCache } from "react-query";
import { useMyTeamsQuery } from "features/auth/queries";
import { ITeamResponse } from "types/Team";
import { setTeam, useUserDispatch, usePermissionsState } from "context";
import { makeIsRowSelectable } from "./utils";

const useManageUsersTableColumns = ({
  selectedRows,
  setSelectedUserToEdit,
  setEditRole,
  teamId,
  refetch,
}: ManageUsersTableColumnParams) => {
  const dispatch = useUserDispatch();
  const { handleAction } = useIsActionAllowed();
  const canViewRoleModal = useCheckPermission(
    FEATURE_ACTION.VIEW,
    FEATURES.MANAGE_USERS_CHANGE_ROLE,
  );
  const queryCache = useQueryCache();
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);

  const { getMyTeams } = useMyTeamsQuery({
    onSuccess: (data: ITeamResponse) => {
      const team = data?.teams?.[0];
      dispatch(setTeam(team));
    },
  });

  const { mutate: fireUnassignSeats } = useUnassignSeatsMutation();
  const { addToast } = useToasts();

  // Spans the whole removal — the DELETE *and* the refetches that follow it.
  // The mutation's own `isLoading` drops as soon as the DELETE resolves, which
  // would re-enable the confirm button while the modal is still open for the
  // refetches, letting the user re-send ids that were just removed.
  const [isRemoving, setIsRemoving] = useState(false);

  const handleSeatUnAssign = () => {
    const userIds = selectedRows?.map((row: { id: any }) => row.id);
    setIsRemoving(true);
    fireUnassignSeats(
      { teamId, userIds },
      {
        onSuccess: async () => {
          queryCache.invalidateQueries([`team-users`, { teamId }]);
          await Promise.all([refetch(), getMyTeams()]);

          // Announce success only now, as the modal closes. Reporting it when
          // the DELETE resolved put a "Success" toast on screen while the modal
          // was still spinning through these refetches.
          setIsRemoving(false);
          setShowConfirmDeleteModal(false);
          addToast("Success", { appearance: "success" });
        },
        onError: () => {
          // The API says what went wrong; refresh so a row that no longer
          // exists stops being offered and the same failure can't repeat. The
          // modal stays open so the user can retry or back out.
          queryCache.invalidateQueries([`team-users`, { teamId }]);
          refetch();
          setIsRemoving(false);
        },
      },
    );
  };
  return [
    {
      id: "name",
      Header: "User",
      accessor: (row: ITeamUserObject) => (
        <ProfileNameCol
          row={row}
          setSelectedUserToEdit={setSelectedUserToEdit}
        />
      ),
      minWidth: 400,
    },
    {
      id: "role",
      Header: "User Role",
      accessor: (row: ITeamUserObject) => {
        const { user_iam_role: userIamRole } = row;
        return (
          <>
            {!!userIamRole && (
              <ChangeRoleColumn
                row={row}
                onClick={selectedRow => {
                  handleAction(
                    canViewRoleModal,
                    FEATURES.MANAGE_USERS_CHANGE_ROLE,
                    () => setEditRole(selectedRow),
                  );
                }}
              />
            )}
          </>
        );
      },
      minWidth: 100,
    },
    {
      id: "cards",
      Header: "Assigned Card",
      accessor: ({ card_ids: cardIds }: ITeamUserObject) => {
        return (
          <Box noOfLines={2} overflow="hidden" width="70%">
            {cardIds?.map((card, index) => (
              <Text key={`${card}-${index}`} display="inline">
                {`${card}`}
                {cardIds.length - (index + 1) !== 0 && ", "}
              </Text>
            ))}
          </Box>
        );
      },
      minWidth: 100,
    },
    {
      id: "actions",
      Header: () => (
        <DeleteUserModal
          selectedRows={selectedRows}
          isLoading={isRemoving}
          isOpen={showConfirmDeleteModal}
          openDeleteModal={() => setShowConfirmDeleteModal(true)}
          closeDeleteModal={() => setShowConfirmDeleteModal(false)}
          handleSeatUnAssign={handleSeatUnAssign}
        />
      ),
      accessor: (row: ITeamUserObject) => (
        <div style={{ maxWidth: "140px", width: "100%" }}>
          <ProfileActionCol row={row} teamId={teamId} refetchUsers={refetch} />
        </div>
      ),
      width: 160,
    },
  ];
};

const ManageUsersTable: FC<IManageUsersTable> = ({
  teamId,
  openAssignSeatsModal,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { user: currentUser } = usePermissionsState();
  const [paginationData, setPaginationData] = useState({
    pageSize: 20,
    offset: 0,
  });

  const [newFilters, setNewFilters] = useState<{
    status?: USER_SEATS_STATUS;
    value?: string;
  }>({});
  const [userTypeFilter, setUserTypeFilter] = useState<{
    filter: USER_IAM_ROLE;
    label: USER_TYPE_LABELS;
  } | null>();

  const {
    data: teamUsers,
    isLoading: isUsersLoading,
    refetch,
  } = useTeamUsersQuery({
    searchQuery,
    teamId,
    limit: paginationData?.pageSize,
    offset: paginationData?.offset,
    ...(userTypeFilter !== null && { permission: userTypeFilter?.filter }),
    ...(newFilters?.status !== null && { status: newFilters?.status }),
  });
  const { data: userTypes } = useTeamUserTypesQuery();
  const users = teamUsers?.results || [];

  const totalDataCount = teamUsers?.count || 0;
  const prevDataCountRef = useRef(totalDataCount);

  // Only record a count we actually have. Paging changes the query key, so the
  // response is briefly undefined and `totalDataCount` reads 0 — storing that
  // would defeat the `isUsersLoading` fallback below, collapse `pageCount` to
  // 0, and trip Table's reset-to-first-page effect. That's what made the first
  // click on "next" bounce back to page 1.
  useEffect(() => {
    if (!isUsersLoading) {
      prevDataCountRef.current = totalDataCount;
    }
  }, [totalDataCount, isUsersLoading]);
  const [editRole, setEditRole] = useState<SelectedRow | null>(null);

  const [selectedRows, setSelectedRows] = useState<ITeamUserObject[]>([]);
  const [selectedUserToEdit, setSelectedUserToEdit] =
    useState<ISelectedUserToEdit | null>(null);
  const columns = useManageUsersTableColumns({
    teamId,
    selectedRows,
    setSelectedUserToEdit: (user: ISelectedUserToEdit) =>
      setSelectedUserToEdit(user),
    setEditRole: (user: SelectedRow) => setEditRole(user),
    refetch,
  });

  const currentUserId = currentUser?.id;
  const isRowSelectable = useMemo(
    () => makeIsRowSelectable(currentUserId),
    [currentUserId],
  );

  // `selectedFlatRows` is a fresh array every render, so this fires constantly.
  // Compare membership (not just the count) so a selection that changed without
  // changing size still syncs, while bailing out when nothing moved — bailing
  // out is what stops the render loop. Set-based, so row order never matters.
  const handleRowSelect = (rows: ITeamUserObject[]) => {
    const currentIds = new Set(selectedRows.map(row => row.id));
    const unchanged =
      rows.length === currentIds.size &&
      rows.every(row => currentIds.has(row.id));

    if (!unchanged) {
      setSelectedRows(rows);
    }
  };

  const handleFetchTableData = ({
    pageIndex,
    pageSize,
  }: {
    pageIndex: any;
    pageSize: any;
  }) => {
    setPaginationData({
      pageSize,
      offset: pageIndex * pageSize,
    });
  };

  // Reset the offset in the same update as the filter that changed. The table
  // also resets its own pageIndex via resetPageKey, but that lands an effect
  // later -- so without this the first request after a filter change still
  // carries the previous page's offset and is thrown away. Both converge on 0,
  // so the query key is unchanged by the time the table's reset arrives.
  const resetOffset = () =>
    setPaginationData(pagination => ({ ...pagination, offset: 0 }));

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    resetOffset();
  };

  const handleStatusFilterChange = (newFilters: any) => {
    setNewFilters(filter => ({ ...filter, ...newFilters }));
    resetOffset();
  };

  const handleUserTypeChange = (permission: TUserTypeFilter | null) => {
    setUserTypeFilter(permission);
    resetOffset();
  };

  const pageCount = Math.ceil(
    (isUsersLoading ? prevDataCountRef.current : totalDataCount) /
      paginationData.pageSize,
  );
  const noResultsText = searchQuery
    ? "Your search did not match any profiles"
    : `You don't have any ${
        newFilters?.status !== null
          ? newFilters.value?.toLowerCase() ===
            FILTER_DISPLAY_VALUES.ACTIVE.toLowerCase()
            ? FILTER_DISPLAY_VALUES.ACTIVE
            : FILTER_DISPLAY_VALUES.PENDING
          : ""
      } ${userTypeFilter?.label ? userTypeFilter?.label : ""}
      profiles in your Team!`;

  return (
    <>
      <Flex flexDirection="column" w="500px">
        <SearchInput defaultValue={searchQuery} onChange={handleSearchChange} />
        <Flex marginY={6}>
          <Box>
            <ManageUsersStatusFilter
              onFiltersChange={handleStatusFilterChange}
            />
          </Box>
          <Box marginLeft={2}>
            <ManageUserTypeFilter
              userTypes={userTypes}
              onUserTypeChange={handleUserTypeChange}
            />
          </Box>
        </Flex>
      </Flex>
      <Table
        columns={columns}
        data={users as any}
        isLoading={isUsersLoading}
        onRowSelect={handleRowSelect}
        isRowSelectable={isRowSelectable}
        // Until we know who is logged in, "select all" could sweep in the
        // admin's own row — hold it back rather than emit a wrong selection.
        canSelectAll={currentUserId !== undefined}
        fetchData={handleFetchTableData}
        pageCount={pageCount}
        resetPageKey={`${searchQuery}|${newFilters?.status ?? ""}|${
          userTypeFilter?.filter ?? ""
        }`}
        footer={
          <Button onClick={openAssignSeatsModal} size="small" block>
            {TEXTS.common.INVITE_USERS}
          </Button>
        }
        noResultsText={noResultsText}
        showPagination
      />

      {selectedUserToEdit && (
        <ChangeInvitationModal
          isOpen={!!selectedUserToEdit}
          onClose={() => setSelectedUserToEdit(null)}
          teamId={teamId}
          selectedUser={selectedUserToEdit}
          refetch={refetch}
        />
      )}
      {editRole && (
        <ChangeRoleModal
          isOpen={!!editRole}
          onClose={() => setEditRole(null)}
          user={editRole!}
          userTypes={userTypes}
          refetch={refetch}
        />
      )}
    </>
  );
};

export default ManageUsersTable;
